import { useContext, useEffect, useState } from "react";
import { authconfig } from "../config";
import { handleLogout, throttle } from "../utils/Utils";
import useUser from "./useUser";
import { AppContext } from "../store/AppProvider";
import { useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import {
	ALLOWED_INDIRECT_ROLES, ERROR_CODES, ID_TOKEN,
	SESSION_LOGOUT_TIME, USERTYPES, USER_ROLES
} from "../utils/constants";
import { getUserRoles } from "../service/user";
import { LOGIN_USER } from "../store/reducer/userReducer";
import { fetchAuthSession } from "aws-amplify/auth";

const cognitoSignature = `CognitoIdentityServiceProvider.${authconfig.Auth.Cognito.userPoolClientId}`;
const lastLoginedIn = () => localStorage.getItem(`${cognitoSignature}.LastAuthUser`);
export const lastLoggedInToken = () => localStorage.getItem(`${cognitoSignature}.${lastLoginedIn()}.idToken`);

function useManageTabSession() {
	const { isInternal, isLoggedIn: isLoggedInThisTab } = useUser();
	const { setUserState } = useContext(AppContext);
	const [loginDone, setLoginDone] = useState(false);
	const navigate = useNavigate();
	const queryclient = useQueryClient();
	const { pathname } = useLocation();
	useEffect(() => {
		if (pathname.includes("callback")) {
			setLoginDone(true);
		} else if (!loginDone) {
			(async () => {
				try {
					await handleCreateTabSession(false, setUserState, isLoggedInThisTab);
					setLoginDone(true);
				} catch (error) {
					switch (error.message) {
						case ERROR_CODES.x400:
							console.log("Token has expired");
						case ERROR_CODES.x403:
							if (isLoggedInThisTab) await handleLogout(isInternal, setUserState, navigate, queryclient);
							setLoginDone(true);
							break;
						default:
							console.log("Something went wrong in useManageTabSession create Tab Session", error);
					}
				}
			})();
		} else {
			setLoginDone(true);
		}
	}, [setUserState, loginDone]);

	useEffect(() => {
		const handleSessionLogout = throttle(async function () {
			const isAuthenticated = lastLoggedInToken();
			if (!isAuthenticated && isLoggedInThisTab) {
				console.log("I m logging you out");
				await handleLogout(isInternal, setUserState, navigate, queryclient);
			}
		}, 300);
		const handleSessionLogin = throttle(async function () {
			const isAuthenticated = lastLoggedInToken();
			if (isAuthenticated && !isLoggedInThisTab) {
				console.log("I m logging you in");
				await handleCreateTabSession(false, setUserState);
			}
		}, 300);
		window.addEventListener("storage", handleSessionLogout);
		window.addEventListener("storage", handleSessionLogin);
		return () => {
			window.removeEventListener("storage", handleSessionLogin);
			window.removeEventListener("storage", handleSessionLogout);
		};
	}, [isLoggedInThisTab]);

	return { loginDone };
}
export default useManageTabSession;

export async function handleCreateTabSession(login, setUserState, isLoggedIn = false) {
	try {
		let payld;
		if (login) {
			const data = await fetchAuthSession();
			if (!data.tokens) {
				throw new Error(ERROR_CODES.x403);
			}
			payld = data.tokens.idToken.payload;
			sessionStorage.setItem(ID_TOKEN, data.tokens.idToken.toString());
		} else {
			const currentIdToken = lastLoggedInToken();
			if (!currentIdToken) {
				throw new Error(ERROR_CODES.x403);
			}
			payld = JSON.parse(atob(currentIdToken.split(".")[1]));
			sessionStorage.setItem(ID_TOKEN, currentIdToken);
		}

		const currentTime = Date.now();
		const expirationTime = payld.exp * 1000;

		// Check token expiration
		if (currentTime >= expirationTime) {
			throw new Error(ERROR_CODES.x400);
		}
		if (isLoggedIn) {
			return;
		}
		const accessRoles = payld?.["custom:accessrole"]?.trim()?.split(",") ?? [];
		// const isIndirect = accessRoles?.some((userRole) => ALLOWED_INDIRECT_ROLES.includes(userRole));
		const isInternal = payld["custom:usertype"]?.toUpperCase() === USERTYPES.INTERNAL;
		const sapAccount = payld["custom:sapAccountid"]?.trim();
		const sapContact = payld["custom:sapcontactid"]?.trim();
		const sapCustGroupCode = payld["custom:customerGroup"]?.trim();
		const isAccountRep = accessRoles?.includes(USER_ROLES.DS_ACTREP);
		const isSuperAdmin = accessRoles?.includes(USER_ROLES.DS_SuperUser);

		let sapUser = {};
		if (!isInternal && sapAccount && sapContact) {
			try {
				sapUser = await getUserRoles(payld["custom:sapAccountid"], payld["custom:sapcontactid"]);
			} catch (error) {
				console.error("Error fetching SAP user roles:", error);
			}
			console.log("sapUser:::::::::", sapUser);
		}

		// Fetch VGSAP permissions for internal users
		// External users default to false (already set above)
		let isSalesNewsAdmin = false;
		let hasSalesNewsAccess = false;
		if (isInternal) {
			try {
				const vgsapPerms = await getUserRoles();
				isSalesNewsAdmin = !!vgsapPerms?.isSalesNewsAdmin;
				hasSalesNewsAccess = !!vgsapPerms?.hasSalesNewsAccess;
			} catch (error) {
				console.error("Error fetching VGSAP permissions:", error);
			}
		}
		
		setUserState({
			type: LOGIN_USER,
			payload: {
				loggedIn: true,
				userInfo: {
					displayName: payld["custom:displayname"],
					family_name: payld["family_name"],
					given_name: payld["given_name"],
					email: payld["email"],
					...(sapContact && sapAccount && {
						sapContactNo: sapContact,
						sapAccountNo: sapAccount,
						sapCustomerGroup: sapCustGroupCode,
					})
				},
				accessrole: accessRoles,
				userType: payld["custom:usertype"],
				status: payld["custom:status"],
				sapUserRole: sapUser?.roles ? [sapUser?.roles] : [],
				customerGroup: sapUser?.customer_group || "",
				salesOrg: sapUser?.sales_organization || "",
				amDetails: sapUser?.am_details || [],
				isAccountRep,
				isSuperAdmin,
				isSalesNewsAdmin,
				hasSalesNewsAccess,
			},
		});
	} catch (error) {
		const knownErrors = new Set(Object.values(ERROR_CODES));
		if (!knownErrors.has(error.message)) {
			console.error("Session creation failed:", error);
		}
		throw error;
	}
}
